import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import apiService from '../services/api';
import { getPortalPathForUser } from '../utils/portalRouting';

/**
 * A route only a staff or superuser account may open.
 *
 * PrivateRoute checks that an `access_token` string exists in localStorage and
 * nothing else — not the role, not the expiry, not even that the string is a
 * token. Every role check therefore lived inside the pages, in a `useEffect`
 * that fires *after* the component has mounted and started fetching, so admin
 * chrome painted and requests went out before anyone asked who was asking.
 *
 * This resolves the role before rendering the page at all. It is still a client
 * guard and is not what keeps data safe — every staff endpoint re-reads the role
 * from the database on each request, which is the real enforcement — but it
 * stops the portal from presenting itself to someone who is not staff.
 *
 * On a network failure `fetchCurrentUser` falls back to the cached user rather
 * than throwing, which is deliberate: a transient blip should not sign a working
 * staff member out, and a stale cache cannot grant access to any actual data.
 */

const RoleRoute = ({ children, require: required = 'staff' }) => {
  const [status, setStatus] = useState('checking');
  const [redirectTo, setRedirectTo] = useState('/login/');

  useEffect(() => {
    let active = true;

    const check = async () => {
      if (!apiService.isAuthenticated()) {
        if (active) {
          setRedirectTo('/login/');
          setStatus('denied');
        }
        return;
      }

      const user = await apiService.fetchCurrentUser();
      if (!active) return;

      const allowed = required === 'superuser'
        ? Boolean(user?.is_superuser)
        : required === 'merchant_pay'
          ? Boolean(user?.is_superuser || user?.is_merchant_specialist)
          : Boolean(user?.is_staff || user?.is_superuser);

      if (allowed) {
        setStatus('allowed');
        return;
      }
      // Send them where they do belong, so a staff user who wandered into a
      // superuser page lands on their own dashboard rather than the login form.
      setRedirectTo(getPortalPathForUser(user));
      setStatus('denied');
    };

    check();
    return () => { active = false; };
  }, [required]);

  if (status === 'checking') {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
        Loading...
      </div>
    );
  }
  if (status === 'denied') {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
};

export default RoleRoute;
